import { Box, ClickAwayListener, IconButton, ITheme, Link, Popper, SvgIcon, TextField, ToggleButton, Tooltip, useTheme } from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../store/reducers/store';
import MultiSelectComponent, { MultiSelectComponentItem } from '../common/MultiSelectComponent';
import { Utilities } from '../../utility/utils';
import { useTranslation } from 'react-i18next';
import { updateFilters } from '../../store/actions/libraryOfFunds';
import ClearIcon from '@mui/icons-material/Clear';
import RangeSelectComponent from '../common/RangeSelectComponent';
import DeleteIcon from '@mui/icons-material/Delete';
import { ensureSectors } from '../../store/actions/createPortfolio';
import { ReactComponent as starIcon } from '../../assets/star_filled.svg';
import { ReactComponent as hourglassIcon } from '../../assets/hourglass.svg';
import { ReactComponent as magnifierIcon } from '../../assets/magnifier.svg';
import { useNavigation, useNavigationType } from 'react-router-dom';

export interface IListOfFundsFiltersProps {
    smallDesign?: boolean;
}

const ListOfFundsFilters: React.FC<IListOfFundsFiltersProps> = ({ smallDesign }) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const theme = useTheme() as ITheme;
    const filters = useAppSelector(state => state.libraryOfFundsReducer.filters);

    const [selectedSectors, setSelectedSectors] = useState<string[]>(filters.sector ? filters.sector : []);
    const [selectedStage, setSelectedStage] = useState<string[]>(filters.stage ? filters.stage : []);
    const [selectedSize, setSelectedSize] = useState<number[]>(filters.size ? filters.size : []);
    const [selectedAera, setSelectedAera] = useState<string[]>(filters.area ? filters.area : []);
    const [selectedELTIF, setSelectedELTIF] = useState<boolean>(filters.eltif ? filters.eltif : false);
    const [selectedNew, setSelectedNew] = useState<boolean>(filters.new ? filters.new : false);
    const [selectedFavorites, setSelectedFavorites] = useState<boolean>(filters.favorites ? filters.favorites : false);
    const [selectedClosing, setSelectedClosing] = useState<boolean>(filters.closingSoon ? filters.closingSoon : false);
    const [searchText, setSearchText] = useState<string>(filters.freeText ? filters.freeText : "");
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

    const isLoaded = useRef<boolean>(false);

    useEffect(() => {
        if (isLoaded.current) {
            dispatch(updateFilters({
                stage: selectedStage,
                sector: selectedSectors,
                area: selectedAera,
                size: selectedSize,
                eltif: selectedELTIF,
                new: selectedNew,
                favorites: selectedFavorites,
                closingSoon: selectedClosing,
                freeText: searchText
            }));
        } else {
            isLoaded.current = true;
        }
    }, [selectedSectors, selectedStage, selectedSize, selectedAera, selectedELTIF, selectedNew, selectedFavorites, selectedClosing, searchText])

    const funds = useAppSelector(state => state.libraryOfFundsReducer.allFunds);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(anchorEl ? null : event.currentTarget);
    };

    const open = Boolean(anchorEl);
    const id = open ? 'simple-popper' : undefined;

    if (funds === undefined) {
        return <></>;
    }
    const sectors: MultiSelectComponentItem[] = [];
    // const sizes: MultiSelectComponentItem[] = [];
    const areas: MultiSelectComponentItem[] = [];
    const sizes = { min: Number.MAX_VALUE, max: 0 };
    for (const fund of funds) {
        for (const sector of fund.sectorDistribution) {
            const newItem = { key: sector.sector, value: sector.sector };
            if (sectors.findIndex(i => i.key === newItem.key) === -1) {
                sectors.push(newItem);
            }
        }
        if (fund.fundSize > sizes.max) {
            sizes.max = fund.fundSize
        }
        if (fund.fundSize < sizes.min) {
            sizes.min = fund.fundSize
        }
        // if (sizes.findIndex(i => i.key === fund.fundSize) === -1) {
        //     sizes.push({ key: fund.fundSize, value: Utilities.toFriendlyMoneyValue(fund.fundSize) });
        // }
        for (const geo of fund.geographicDistribution) {
            if (areas.findIndex(i => i.key === geo.zone) === -1) {
                areas.push({ key: geo.zone, value: geo.zone });
            }
        }
    }
    sectors.sort((a, b) => (a.key as string).localeCompare(b.key));
    // sizes.sort((a, b) => b.key - a.key);
    areas.sort((a, b) => (a.key as string).localeCompare(b.key));

    const stages: MultiSelectComponentItem[] = [{ key: "Early", value: "Early" }, { key: "Growth", value: "Growth" }, { key: "Late", value: "Late" }];
    // const size: 

    const updateSectors = (sectors: string[]) => {
        setSelectedSectors(sectors);
        if (smallDesign) {
            //Ensure values
            dispatch(ensureSectors(sectors));
        }
    }

    const clearTextSearch = () => {
        setSearchText("");
    }

    const sizeRangeSelected = (range: number[] | undefined): void => {
        setSelectedSize(range ? range : []);
    }

    const clearSearch = () => {
        setSelectedSectors([]);
        setSelectedStage([]);
        setSelectedSize([]);
        setSelectedAera([]);
        setSelectedELTIF(false);
        setSelectedNew(false);
        setSelectedFavorites(false);
        setSelectedClosing(false);
    }

    return <Box display={"flex"} width={"100%"} gap={smallDesign ? "8px" : "16px"} alignItems={"stretch"} flexWrap={(smallDesign ? "wrap" : undefined)}>
        <MultiSelectComponent label={t('LibraryOfFundFilterSector')} onSelectionChange={updateSectors} selectedValues={selectedSectors} values={sectors} width={smallDesign ? "106px" : undefined} />
        <MultiSelectComponent label={t('LibraryOfFundFilterStage')} onSelectionChange={setSelectedStage} selectedValues={selectedStage} values={stages} width={smallDesign ? "99px" : undefined} />
        <RangeSelectComponent label={t('LibraryOfFundFilterSize')} min={sizes.min} max={sizes.max} step={1000000} onRangeSelected={sizeRangeSelected} selection={selectedSize} width={smallDesign ? "122px" : undefined} />
        {/* <MultiSelectComponent label={t('LibraryOfFundFilterSize')}  onSelectionChange={setSelectedSize} selectedValues={selectedSize} values={sizes} width={smallDesign ? "84px" : undefined} renderValue={a => a.map(b => Utilities.toFriendlyMoneyValue(b)).join(', ')} /> */}
        <MultiSelectComponent label={t('LibraryOfFundFilterGeography')} onSelectionChange={setSelectedAera} selectedValues={selectedAera} values={areas} width={smallDesign ? "133px" : undefined} />
        <Tooltip title={t('LibraryOfFundsELTIFToolTip')} arrow>
            <ToggleButton size='small' value={t('LibraryOfFundFilterELTIF')} selected={selectedELTIF} onChange={() => setSelectedELTIF(!selectedELTIF)}>ELTIF 2.0</ToggleButton>
        </Tooltip>
        <Tooltip title={t('LibraryOfFundFilterNewToolTip')} arrow>
            <ToggleButton size='small' value={t('LibraryOfFundFilterNew')} selected={selectedNew} onChange={() => setSelectedNew(!selectedNew)}>{t('LibraryOfFundFilterNew')}</ToggleButton>
        </Tooltip>
        <Box display={"flex"} gap={"8px"} alignItems={"center"}>
            <Tooltip title={t('LibraryOfFundFilterFavoritesToolTip')} arrow>
                <ToggleButton size='small' value={t('LibraryOfFundFilterFavorites')} selected={selectedFavorites} onChange={() => setSelectedFavorites(!selectedFavorites)} aria-label={t('A11yFilterByFavorites')}><SvgIcon component={starIcon} inheritViewBox sx={{ width: "18px" }} /></ToggleButton>
            </Tooltip>
            <Tooltip title={t('LibraryOfFundFilterClosingSoonToolTip')} arrow>
                <ToggleButton size='small' value={t('LibraryOfFundFilterTime')} selected={selectedClosing} onChange={() => setSelectedClosing(!selectedClosing)} aria-label={t('A11yFilterByClosingSoon')}><SvgIcon component={hourglassIcon} inheritViewBox sx={{ width: "18px" }} /></ToggleButton>
            </Tooltip>
            <ToggleButton size='small' value={t('LibraryOfFundFilterSearch')} selected={searchText !== ""} aria-describedby={id} type="button" onClick={handleClick} aria-label={t('A11yFilterByText')}><SvgIcon component={magnifierIcon} inheritViewBox sx={{ width: "18px" }} /></ToggleButton>
            {open && <ClickAwayListener onClickAway={(e) => handleClick(e as any)}>
                <Popper id={id} open={open} anchorEl={anchorEl} popperOptions={{ placement: 'bottom-end' }} sx={{
                    boxShadow: "0px 12px 16px -4px #10182814",
                    backgroundColor: "#ffffff",
                    borderRadius: "8px",
                    overflow: "hidden"
                }}>
                    <Box display={"flex"} padding={"10px"} justifyContent={"space-between"} alignItems={"center"} gap={"5px"}>
                        <TextField value={searchText} label={t('LibraryOfFundFilterTextSearch')} onChange={(e) => setSearchText(e.target.value as string)} sx={{ width: "400px", backgroundColor: theme.palette.primary.light}}
                        InputLabelProps={{ style: {fontWeight: "400"}}}
                            InputProps={{
                                endAdornment: <IconButton onClick={clearTextSearch} aria-label={t('A11yClearTextFilter')}>
                                    <DeleteIcon />
                                </IconButton>
                            }}
                            inputProps={{ style: { fontWeight: "400"}}} />
                        <IconButton onClick={handleClick}>
                            <ClearIcon />
                        </IconButton>
                    </Box>
                </Popper>
            </ClickAwayListener>}
            <Link onClick={clearSearch} sx={{ color: theme.palette.primary.dark, cursor: "pointer" }}>Clear All</Link>
        </Box>
    </Box>
}

export default ListOfFundsFilters